// Seamless-world runtime — pure data + lookups.
//
// The mega-map (`world.runtime.json`, produced by packages/emerald-converter's
// stitch pass) is one flat tilemap with metadata-tagged AABB regions inside
// it. This module owns the pure data shape + point-in-rectangle lookup with
// a `lastHit` cache. The Phaser-coupled load path lives in
// `apps/game/src/render/worldLoader.ts` so unit tests (no DOM, no Phaser)
// can drive this module's functions directly — same split as
// followerState.ts (pure) vs follower.ts (Phaser-coupled).
//
// Module surface (intentionally small, per design doc Section 1):
//   - parseWorldData(raw): construct the WorldData from raw JSON
//   - regionAt(world, gx, gy): PIR lookup with cache, O(1) steady-state
//   - crossIntoRegion(world, newRegion, prevRegion): cross handler stub
//
// Step 2 (this commit, STRUCTURAL): module exists + unit-tested.
// Step 3 wires regionAt into WorldScene's exterior boundary handler.
// Step 4 fills in music crossfade + route placard inside crossIntoRegion.

// ---------------------------------------------------------------------------
// Data shapes
// ---------------------------------------------------------------------------

export interface RegionWarp {
  id: string;
  /** Warp tile in mega-map (global) coords. */
  globalX: number;
  globalY: number;
  elevation: number;
  /** Destination map name — typically an interior runtime.json filename. */
  destinationMap: string;
  /** Warp index inside the destination map's warps[] array. */
  destinationWarp: number;
}

export interface RegionNpc {
  localId?: string;
  graphicsId: string;
  /** Home tile in mega-map (global) coords. */
  x: number;
  y: number;
  elevation: number;
  movementType: string;
  /** Wander radius around the home tile (relative; runtime clamps to the
   *  owning region's AABB per the design doc's NPC-at-edge rule). */
  movementRangeX: number;
  movementRangeY: number;
  trainerType: string;
  trainerSightOrBerryTreeId: number;
  scriptRef: string;
  flag: string;
}

export interface RegionSign {
  type: string;
  x: number;
  y: number;
  elevation: number;
  playerFacingDir: string;
  scriptRef: string;
}

export interface RegionTrigger {
  type: string;
  x: number;
  y: number;
  elevation: number;
  var: string;
  varValue: number;
  scriptRef: string;
}

export interface Region {
  /** Short cartridge name (e.g. "Route1") — runtime-canonical identifier
   *  and the key that data tables in apps/game/src/data/ will use after
   *  Step 3's rekey pass. */
  name: string;
  /** Cartridge long form (e.g. "MAP_ROUTE1") preserved for backwards
   *  compatibility with state that hasn't been rekeyed yet + with the
   *  v7→v8 destructive migration's diagnostic logging. */
  id: string;
  /** Half-open AABB in global tile coords: [x, x+w) × [y, y+h). Adjacent
   *  regions are strictly adjacent — never overlapping. */
  x: number;
  y: number;
  w: number;
  h: number;
  warps: RegionWarp[];
  events: {
    npcs: RegionNpc[];
    signs: RegionSign[];
    triggers: RegionTrigger[];
  };
}

/** Pure-data view of the world — no Phaser handles. Tests construct this
 *  directly with synthetic regions. The Phaser-coupled `World` in
 *  render/worldLoader.ts extends this with tilemap + layer handles. */
export interface WorldData {
  worldWidth: number;
  worldHeight: number;
  tileSize: number;
  /** Flat row-major arrays, length = worldWidth * worldHeight. Index 0 in
   *  metatileIds = transparent void tile. */
  metatileIds: number[];
  metatileIdsAbove: number[];
  collision: number[];
  behaviors: number[];
  layerTypes: number[];
  regions: Region[];
  /** O(1) lookup by region.name (e.g. "Route1") — used by Step 3's
   *  destinationMap → region resolution for warps from interiors. */
  byName: Map<string, Region>;
  /** PIR-lookup cache — mutated by regionAt as a side effect. Reset to
   *  null on void hits so a future probe doesn't return a stale region. */
  lastHit: { region: Region; x: number; y: number } | null;
}

/** Raw JSON shape the stitcher emits — matches
 *  packages/emerald-converter/src/stitchExteriors.ts's WorldRuntimeJson. */
export interface RawWorldJson {
  atlasImage: string;
  atlasAboveImage: string;
  atlasCols: number;
  atlasRows: number;
  atlasAboveCols: number;
  atlasAboveRows: number;
  tileSize: number;
  worldWidth: number;
  worldHeight: number;
  metatileIds: number[];
  metatileIdsAbove: number[];
  collision: number[];
  behaviors: number[];
  layerTypes: number[];
  regions: Region[];
}

// ---------------------------------------------------------------------------
// Pure functions
// ---------------------------------------------------------------------------

/** Parse a raw world JSON into the pure WorldData shape. The `byName`
 *  lookup map + lastHit cache are initialized here. */
export function parseWorldData(raw: RawWorldJson): WorldData {
  const byName = new Map<string, Region>();
  for (const r of raw.regions) byName.set(r.name, r);
  return {
    worldWidth: raw.worldWidth,
    worldHeight: raw.worldHeight,
    tileSize: raw.tileSize,
    metatileIds: raw.metatileIds,
    metatileIdsAbove: raw.metatileIdsAbove,
    collision: raw.collision,
    behaviors: raw.behaviors,
    layerTypes: raw.layerTypes,
    regions: raw.regions,
    byName,
    lastHit: null,
  };
}

/**
 * Point-in-rectangle lookup against half-open AABBs, with a `lastHit`
 * cache. Steady-state walking (player stays inside the cached region) is
 * O(1); boundary cross is one O(N) scan where N = regions.length (≤ ~37
 * at full Kanto, 5 at v0.1 scope).
 *
 * Returns null when the point is outside every region (e.g. void cells
 * between non-adjacent regions, or past the world bounds). The cache is
 * cleared on void hits so a future probe re-scans rather than returning
 * a stale cached region.
 *
 * Side effect: mutates `world.lastHit`. Callers don't need to manage it.
 */
export function regionAt(world: WorldData, globalX: number, globalY: number): Region | null {
  const cached = world.lastHit;
  if (cached && contains(cached.region, globalX, globalY)) {
    return cached.region;
  }
  for (const r of world.regions) {
    if (contains(r, globalX, globalY)) {
      world.lastHit = { region: r, x: globalX, y: globalY };
      return r;
    }
  }
  world.lastHit = null;
  return null;
}

function contains(r: Region, x: number, y: number): boolean {
  return x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h;
}

/**
 * Cross-into-region handler. Step 2 (this commit) is a STRUCTURAL stub:
 * the function exists and updates the PIR cache, but no observable side
 * effects fire yet.
 *
 * Step 4 will add inside this same body:
 *   - audio.crossfade(prevRegion.music → newRegion.music, 800ms equal-power)
 *     fired only when newRegion.music !== prevRegion?.music (silent crosses
 *     between adjacent regions sharing a BGM key are explicit in P3).
 *   - scene.showRoutePlacard(newRegion.name) — canvas-anchored DOM glass pill
 *     (var(--glass-fill-mid) + DM Sans 13/600 uppercase var(--text-secondary)),
 *     fade in 200ms / dwell 1800ms / fade out 200ms.
 *
 * Fire-and-forget; idempotent under rapid re-fire. Speedrun boundary
 * crosses supersede the previous cross — the audio system's existing
 * crossfade supports interrupt-and-continue from current dB level, and
 * the placard's tween chain will cancel-and-restart cleanly.
 */
export function crossIntoRegion(
  world: WorldData,
  newRegion: Region,
  prevRegion: Region | null,
): void {
  if (prevRegion !== null && newRegion === prevRegion) return;
  // Touch the cache so the next regionAt call returns this region O(1).
  world.lastHit = { region: newRegion, x: -1, y: -1 };
}
